import { useCallback, useEffect, useRef, useState } from 'react';

import { logger } from 'common/services';
import { configureAnalyser, createLevelMeter, sampleAnalyser } from 'pages/HealthCheck/utils/microphoneLevel';

import { getSpeechRecognitionCtor, markSpeechUnsupportedForSession } from './speechRecognition';

import type { SpeechRecognitionLike } from './speechRecognition';

export type DictationStatus = 'unsupported' | 'idle' | 'starting' | 'listening' | 'blocked' | 'unheard';

export type UseSpeechDictation = {
  status: DictationStatus;
  level: number;
  start: () => void;
  finish: () => void;
  discard: () => void;
};

type Options = { lang: string; onTranscript: (text: string) => void };

type Meter = { stream: MediaStream; context?: AudioContext; timer?: number };

const UNSUPPORTED_ERRORS = new Set(['network', 'service-not-allowed', 'language-not-supported']);
// The tick `createLevelMeter`'s attack and release are tuned for.
const LEVEL_INTERVAL_MS = 50;

// Only the error's name is logged, never its message, so no user content can reach the logs.
const errorName = (error: unknown) =>
  error instanceof Error || error instanceof DOMException ? error.name : typeof error;

const detach = (recognition: SpeechRecognitionLike) => {
  recognition.onstart = null;
  recognition.onresult = null;
  recognition.onerror = null;
  recognition.onend = null;
};

export const useSpeechDictation = ({ lang, onTranscript }: Options): UseSpeechDictation => {
  const [status, setStatus] = useState<DictationStatus>(() => (getSpeechRecognitionCtor() ? 'idle' : 'unsupported'));
  const [level, setLevel] = useState(0);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const heardRef = useRef(false);
  const endReasonRef = useRef<'finish' | 'discard' | 'error' | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const meterRef = useRef<Meter | null>(null);

  const stopMeter = useCallback(() => {
    const meter = meterRef.current;
    meterRef.current = null;
    if (!meter) return;
    window.clearInterval(meter.timer);
    meter.stream.getTracks().forEach((track) => track.stop());
    void meter.context?.close().catch(() => undefined);
    setLevel(0);
  }, []);

  // The level only drives the waveform. If the meter can't start, dictation still works and the
  // waveform shows a resting line.
  const startMeter = useCallback(async (recognition: SpeechRecognitionLike) => {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      // A refused mic surfaces through the recognition's own `not-allowed` error; this only loses the waveform.
      logger.debug('[useSpeechDictation] Level meter mic unavailable', { error: errorName(error) });
      return;
    }
    // The recording may have ended (or the dialog closed) while the mic prompt was open. Release the
    // stream straight away, or the browser's recording indicator stays on.
    if (recognitionRef.current !== recognition) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    const meter: Meter = { stream };
    meterRef.current = meter;
    try {
      const context = new AudioContext();
      meter.context = context;
      const analyser = context.createAnalyser();
      configureAnalyser(analyser);
      context.createMediaStreamSource(stream).connect(analyser);
      const samples = new Float32Array(analyser.fftSize);
      // The sound check's meter: scaled to the recent loudest moment, so a normal speaking voice fills the
      // waveform on a quiet browser too, and smoothed so it glides rather than flickers. Raw amplitude kept
      // ordinary speech down at a few percent, which drew dots.
      const toLevel = createLevelMeter();
      meter.timer = window.setInterval(() => {
        // The meter reads 0-100; this turns it into 0-1 in 0.05 steps, so an unchanged reading bails out of the
        // state update instead of re-rendering the question at the meter's tick rate.
        setLevel(Math.round(toLevel(sampleAnalyser(analyser, samples)) / 5) / 20);
      }, LEVEL_INTERVAL_MS);
    } catch (error) {
      logger.warn('[useSpeechDictation] Level meter unavailable', { error: String(error) });
    }
  }, []);

  const start = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setStatus('unsupported');
      return;
    }
    // Browsers fire `onend` asynchronously after stop()/abort(), so a previous recording may still be
    // winding down. Detach it so its late events can't touch the new one, and release its mic.
    const previous = recognitionRef.current;
    if (previous) {
      detach(previous);
      previous.abort();
      recognitionRef.current = null;
    }
    stopMeter();

    const recognition = new Ctor();
    const isCurrent = () => recognitionRef.current === recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;
    heardRef.current = false;
    endReasonRef.current = null;

    recognition.onstart = () => {
      if (isCurrent()) setStatus('listening');
    };
    recognition.onresult = (event) => {
      if (!isCurrent()) return;
      const text = Array.from(event.results, (result) => result[0]?.transcript ?? '').join('');
      if (text.trim()) heardRef.current = true;
      onTranscriptRef.current(text.trim());
    };
    recognition.onerror = ({ error }) => {
      if (!isCurrent() || error === 'aborted') return;
      endReasonRef.current = 'error';
      if (error === 'not-allowed') setStatus('blocked');
      else if (error === 'no-speech') setStatus('unheard');
      else if (UNSUPPORTED_ERRORS.has(error)) {
        markSpeechUnsupportedForSession();
        setStatus('unsupported');
      } else {
        logger.warn('[useSpeechDictation] Recognition error', { error });
        setStatus('idle');
      }
    };
    recognition.onend = () => {
      if (!isCurrent()) return;
      recognitionRef.current = null;
      stopMeter();
      const reason = endReasonRef.current;
      if (reason === 'error') return;
      if (reason === 'discard') setStatus('idle');
      else setStatus(heardRef.current ? 'idle' : 'unheard');
    };

    recognitionRef.current = recognition;
    setStatus('starting');
    void startMeter(recognition);
    try {
      recognition.start();
    } catch (error) {
      detach(recognition);
      recognitionRef.current = null;
      stopMeter();
      logger.warn('[useSpeechDictation] Recognition failed to start', { error: errorName(error) });
      setStatus('idle');
    }
  }, [lang, startMeter, stopMeter]);

  const finish = useCallback(() => {
    endReasonRef.current = 'finish';
    recognitionRef.current?.stop();
  }, []);

  const discard = useCallback(() => {
    endReasonRef.current = 'discard';
    recognitionRef.current?.abort();
  }, []);

  useEffect(
    () => () => {
      endReasonRef.current = 'discard';
      recognitionRef.current?.abort();
      // Real browsers fire `onend` asynchronously; clear the ref now so a mic prompt that resolves
      // after unmount releases its stream instead of storing it.
      recognitionRef.current = null;
      stopMeter();
    },
    [stopMeter]
  );

  return { status, level, start, finish, discard };
};
